import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataTableComponent } from 'src/app/components';
import { asArray, ClientStatus, HttpMethod, UserAction } from 'src/app/enum';
import { AuthenticationService, DataTableService } from 'src/app/_services';
import { ClientsService, IClientListFormData, IClientListItem } from 'src/app/_services/clients.service';
import { LxmMessage } from 'src/app/_helpers';
import { FormControl, FormGroup } from '@angular/forms';
import { LOCAL_STORAGE_KEY } from 'src/app/config';
import { ClientsListViewType } from '../../enum/ClientsListViewType';
import { ClientsListState } from '../../state/clients-list.state';
import { copyToClipboard } from 'src/app/_helpers/utilities';

@Component({
  selector: "clients-list-card",
  templateUrl: "./clients-list.card.html",
  host: { class: "data-table-card" },
  styleUrls: ["./clients-list.card.scss"]
})
export class ClientsListCard
  extends DataTableComponent<IClientListItem, any>
  implements OnInit, OnDestroy
{
  public LOCAL_STORAGE_KEY = LOCAL_STORAGE_KEY.PAGE_SIZE_CLIENTS_LIST;

  @Output()
  public loaded: EventEmitter<boolean> = new EventEmitter();

  @Input()
  public search: Observable<any>;
  private searchData: any;

  public formData: IClientListFormData;
  public clientTagsById: any;

  public dataUrl = "clients/griddata";
  public method = HttpMethod.Post;
  public UserAction = UserAction;
  public customFilter: string = this.state.listFilter;

  public ClientsListViewType = ClientsListViewType;

  public table = {
    columns: []
  };

  protected colPropertyMap = {
    'client_name': 'name',
    'last_event': 'lastEventDate',
    'upcoming_event': 'upcomingEventDate',
    'client_address': 'address',
    'client_reg_code': 'regNo',
    'client_status': 'status',
    'client_is_retailer': 'isSystemClient',
    'client_is_store_client': 'isStoreClient',
    'client_visible_in_products_list': 'visibleInProductsList'
  };

  private _cols: string[];
  private destroy$ = new Subject<void>();

  public statusOptions = asArray(ClientStatus);

  public filterTypeOptions = [
    {
      id: ClientsListViewType.All,
      label: "cards.clients.clients_list.filters.all"
    },
    {
      id: ClientsListViewType.Retailers,
      label: "cards.clients.clients_list.filters.retailers"
    },
    {
      id: ClientsListViewType.Partners,
      label: "cards.clients.clients_list.filters.partners"
    },
    {
      id: ClientsListViewType.Owned,
      label: "cards.clients.clients_list.filters.my_clients"
    }
  ];

  constructor(
    private _clientService: ClientsService,
    private _lxmMessage: LxmMessage,
    private _authService: AuthenticationService,
    public state: ClientsListState,
    route: ActivatedRoute,
    dataService: DataTableService<IClientListItem, any>,
    cd: ChangeDetectorRef
  ) {
    super(dataService, cd);
    this.initialData = route.snapshot.data.initialData;
    this.customFilter = this.state.listFilter;
    this.formData = route?.snapshot?.data?.formData as IClientListFormData;
    this.clientTagsById = this.formData.clientTags.reduce(
      (a, v) => ({ ...a, [v.id]: v }),
      {}
    );

    this._cols = [
      "client_image",
      "client_name",
      "client_tags",
      "client_reg_code",
      "client_is_retailer",
      "client_is_store_client",
      "upcoming_event"
    ];

    if (this._authService.hasRight([UserAction.ManageOthersClients])) {
      this._cols.push("client_visible_in_products_list");
    }

    this._updateColumns();
  }

  private _updateColumns() {
    if (this.state.listViewType == ClientsListViewType.Partners) {
      this.table.columns = this._cols.filter(
        (x) =>
          x != "client_is_retailer" && x != "client_visible_in_products_list"
      );
    } else {
      this.table.columns = this._cols;
    }
  }

  public ngOnInit() {
    super.ngOnInit();

    this.loadAfterViewInit = this.search === null;

    this.dataSource.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: boolean) => {
        this.state.isSearchLoading = val;
      });

    this.state.controls.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(x => {
        const status = this.state.controls.get('statusFilter').value;
        const viewType = this.state.controls.get('listFilterType').value;
        this.setListView(status, viewType);
      });
  }

  public get isLoading() {
    return this.state.isSearchLoading;
  }

  public get controls() {
    return this.state.controls;
  }

  public createRowFormGroup(row) {
    let visibleInProductsListControl = new FormControl(
      row.visibleInProductsList
    );

    let fg = new FormGroup({
      visibleInProductsList: visibleInProductsListControl
    });

    fg.get("visibleInProductsList").valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this._clientService.toggleVisibilityInProductsList(row.id, x).result(
          null,
          (_) => {
            this._lxmMessage.ok({
              message: "client.client_visibility_changed"
            });
          },
          (err) => {
            visibleInProductsListControl.setValue(false, { emitEvent: false });
            this._lxmMessage.error({
              message: err.validationSummary,
              args: err.args
            });
          }
        );
      });

    return fg;
  }

  public isListViewActive(view: ClientsListViewType) {
    return this.state.listViewType === view;
  }

  public setListView(status: ClientStatus, viewType: ClientsListViewType) {
    if (this.isLoading || (viewType === this.state.listViewType && status === this.state.status)) {
      return;
    }

    this.state.setListView(status, viewType);
    
    this.customFilter = this.state.listFilter;
    
    this.resetPaginator();
    this._updateColumns();

    this.load(null, () => {
      this._cd.detectChanges();
    });
  }

  public copyCode(e: Event, value: string) {
    e.stopPropagation();
    copyToClipboard(value);
  }

  public onSearch(searchData) {
    this.searchData = searchData;
  }

  public onLoad() {
    this.load();
  }

  public limitTags(row: IClientListItem) {
    return row.clientTagIds?.slice(0, 2);
  }

  public getTagClass(tagId: string) {
    const tag = this.clientTagsById[tagId];
    let css = "color-tag";

    if (tag) {
      css = `${css} color-${tag.color}`;
    }

    return css;
  }

  protected getData() {
    return this.searchData;
  }

  public get hasClients() {
    return this.dataSource.data?.length ? true : false;
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
